// api/restaurantsApi.js
import { api } from "api";
import {
    getAllRestaurants,
    addRestaurant,
    editRestaurant,
    addDeliveryCostForVendor,
} from "api/apiURLs";

export const getVendors = async (params = {}) => {
    const response = await api.get(getAllRestaurants, { params });
    return response.data;
};

export const getVendorsDropdown = async (params = {}) => {
    const response = await api.get("vendor/get-vendors-dropdown", { params });
    return response.data;
};

export const getVendorById = async (id) => {
    const response = await api.get(`vendor/get-vendor/${id}`);
    return response.data;
};

export const getVendorSchedule = async (id) => {
    const response = await api.get(`vendor/weekly-schedule/${id}`);
    return response.data;
};

export const createVendor = async (data) => {
    const response = await api.post(addRestaurant, data);
    return response.data;
};

export const createCosts = async (data) => {
    const response = await api.post(addDeliveryCostForVendor, data);
    return response.data;
};

export const copyCosts = async (data) => {
    const response = await api.post("api/copy-costs", data);
    return response.data;
};

export const editVendor = async (data) => {
    const response = await api.patch(editRestaurant, data);

    return response.data;
};

/**
 * Changes a vendor's password, and nothing else.
 *
 * Deliberately NOT part of `editVendor`. While the password rode along in that
 * mass-assigned body, the edit form submitted its password inputs on every save
 * and an empty one overwrote the stored hash with "", locking the vendor out of
 * their own dashboard silently. A dedicated call means a password is only ever
 * sent when someone set out to change one.
 *
 * @param {number|string} userId - The vendor's USER id.
 * @param {{password: string, confirm_password: string}} data
 */
export const updateVendorPassword = async (userId, data) => {
    const response = await api.patch(`vendor/${userId}/password`, data);
    return response.data;
};

/**
 * Pins a restaurant to a place in the customer lists; `null` removes the pin.
 * `userId` is the restaurant's `users.id`, the `id` of a row in the table.
 */
/**
 * Moves a restaurant to another city, with its delivery prices: the old
 * city's prices are removed and every area of the new city gets `cost`.
 * Super admins only. See api util/vendorCityMove.js.
 *
 * @param {number|string} userId - the restaurant's user id
 * @param {number|string} cityId
 * @param {number} cost - ₪ for each new area
 * @returns {Promise<{fromCityId: number, toCityId: number, removedCosts: number, addedCosts: number, keptCosts: number}>}
 */
export const moveVendorCity = async (userId, cityId, cost) => {
    const response = await api.patch(`vendor/${userId}/city`, { cityId, cost });
    return response.data;
};

export const updateVendorDisplayRank = async (userId, rank) => {
    const response = await api.patch(`vendor/${userId}/display-rank`, { rank });

    return response.data;
};

export const updateVendorOrderPercentage = async (userId, orderPercentage) => {
    const response = await api.patch(`vendor/${userId}/order-percentage`, {
        order_percentage: orderPercentage,
    });

    return response.data;
};

export const editSchudle = async (data) => {
    const response = await api.patch("vendor/weekly-schedule/bulk", data);

    return response.data;
};

export const editVendorSchudle = async (id, data) => {
    const response = await api.put("vendor/weekly-schedule/" + id, data);

    return response.data;
};

/**
 * Removes a restaurant.
 *
 * Was the generic `DELETE /user/delete/:id`, which on any restaurant with a
 * settlement was refused by the database (statements.vendor_id is RESTRICT) and
 * surfaced as an unexplained error — and on one without a settlement went
 * through and silently detached its order history (orders.vendorId is SET
 * NULL). The vendor endpoint counts what the store owns first: an empty one is
 * really deleted, one with history is archived instead.
 *
 * Resolves for BOTH outcomes — archiving is a successful removal, not a
 * failure. Read `deleted` to tell them apart and `message` for the Arabic
 * explanation of why, which the API builds from the actual counts.
 *
 * @param {number|string} id - The vendor's USER id (what the list renders).
 * @returns {Promise<{deleted: boolean, message: string, history: Record<string, number>}>}
 */
export const deleteVendor = async (id) => {
    const response = await api.delete(`vendor/delete-vendor/${id}`);
    return response.data;
};
export const deleteAreaCost = async (id) => {
    const response = await api.delete(`api/costs/${id}`);
    return response.data;
};
